import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import Paginator from '../../components/common/Paginator';
import Layout from '../../components/layouts/Layout';
import Loader from '../../components/common/Loader';

import useCostCentreRequest from '../../requests/CostCentreRequest';
import useUserRequest from '../../requests/UsersRequest';

import { AccountRoutes } from '../../routes/AccountRoutes';

import Config from '../../Config';

import FormattedMessages from '../../messages/account/UsersFormattedMessages';

import { buildHash, parseHashFromProps } from '../../utils/pagination';

const WebSMSUsers = ({ customer, accessLevel, accessType, errorHandler }) => {
  const { getUsers } = useUserRequest();
  const { getCentres } = useCostCentreRequest();
  const location = useLocation();
  const navigate = useNavigate();

  if (accessLevel <= 0 || accessType === 0) return null;

  const { page, letter } = parseHashFromProps({ location });

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [addNew, setAddNew] = useState(false);
  const [pageNumber, setPageNumber] = useState(page || 1);
  const [filterLetter, setFilterLetter] = useState(letter);
  const [totalCount, setTotalCount] = useState(0);
  const [pageLength, setPageLength] = useState(Config.defaultPL);
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState(location?.state?.email || '');
  const [numberFilter, setNumberFilter] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({
    name: '',
    email: location?.state?.email || '',
    number: '',
  });

  const setHash = () => {
    const hash = buildHash(pageNumber, filterLetter);
    if (window.location.hash !== hash) {
      window.location.hash = hash;
    }
  };

  const load = async () => {
    setHash();
    setLoading(true);
    try {
      // Ensure pageNumber is at least 1 for API calls
      const effectivePageNumber = Math.max(1, pageNumber);

      const [usersData, costCentresData] = await Promise.all([
        getUsers(
          effectivePageNumber,
          pageLength,
          nameFilter,
          emailFilter,
          numberFilter,
          errorHandler
        ),
        getCentres(errorHandler),
      ]);

      if (!usersData || typeof usersData !== 'object') {
        console.error('Invalid users data received:', usersData);
        throw new Error('Invalid users data received');
      }

      // Handle both array and object responses
      const items = Array.isArray(usersData)
        ? usersData
        : usersData.items || [];
      const total = Array.isArray(usersData)
        ? items.length
        : usersData.rowCount || items.length;

      setUsers(items);
      setCostCentres(costCentresData);
      setTotalCount(total);
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filterLetter, pageNumber, pageLength, searchParams]);

  useEffect(() => {
    if (page !== pageNumber) setPageNumber(page);
    if (letter !== filterLetter) setFilterLetter(letter);
  }, [location]);

  const pageChange = (newPageIndex) => {
    setPageNumber(newPageIndex);
  };

  const changePageLength = (event) => {
    const newLength = parseInt(event.target.value, 10);
    setPageLength(newLength);
    setPageNumber(1); // Reset to first page when changing page length
  };

  const handleSearch = () => {
    setIsSearching(true);

    // Only include non-empty search params
    const newSearchParams = {
      name: nameFilter.trim(),
      email: emailFilter.trim(),
      number: numberFilter.trim(),
    };

    // Update search params
    setSearchParams(newSearchParams);

    // Reset pagination
    setPageNumber(1);
    setIsSearching(false);
  };

  const handleClearSearch = () => {
    // Clear all filters
    setNameFilter('');
    setEmailFilter('');
    setNumberFilter('');
    setSearchParams({
      name: '',
      email: '',
      number: '',
    });
    setPageNumber(1);
  };

  const renderUsersTable = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={7} sx={{ paddingY: 4, textAlign: 'center' }}>
            <Loader />
          </TableCell>
        </TableRow>
      );
    }

    if (!users || users.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} sx={{ paddingY: 2 }}>
            <Typography>{FormattedMessages.noUsersFound}</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return users.map((user, index) => (
      <TableRow key={index} onClick={() => handleRowClick(user)} hover>
        <TableCell>{user?.user_name || '-'}</TableCell>
        <TableCell>{user?.user_email || '-'}</TableCell>
        <TableCell>{user?.user_tn || '-'}</TableCell>
        <TableCell>
          {user?.user_status === 'A'
            ? FormattedMessages.active
            : FormattedMessages.inactive}
        </TableCell>
        <TableCell>{user?.user_type === 'P' ? 'Admin' : 'User'}</TableCell>
        <TableCell>{user?.max_sms_per_day || '-'}</TableCell>
        <TableCell>
          {user?.cost_centre_name ||
            costCentres.find(
              (centre) => centre.cost_centre_id === user?.cost_centre_id
            )?.cost_centre_name ||
            '-'}
        </TableCell>
      </TableRow>
    ));
  };

  const handleRowClick = (user) => {
    if (!loading) {
      navigate(`/account/users/${user.customer_user_id}`);
    }
  };

  const handleCloseEdit = async (refresh = false) => {
    navigate('/account/users');
    if (refresh) {
      await load();
    }
  };

  return (
    <Layout
      routes={AccountRoutes}
      title={FormattedMessages.completeSMSUsers}
      accessLevel={accessLevel}
      accessType={accessType}
      isHidden={!customer.hasCampaign}
    >
      <Card
        elevation={0}
        sx={{
          marginBottom: 3,
          backgroundColor: 'common.white',
        }}
      >
        <CardContent sx={{ paddingLeft: 0 }}>
          <Typography
            variant="h6"
            color="secondary"
            sx={{
              marginBottom: 2,
              fontSize: '0.875rem',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}
          >
            {FormattedMessages.searchForUsers}
          </Typography>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{
                width: { xs: '100%', md: '75%' },
                marginX: 'auto',
              }}
            >
              <TextField
                label={FormattedMessages.name}
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                disabled={loading || isSearching}
              />
              <TextField
                label={FormattedMessages.email}
                value={emailFilter}
                onChange={(e) => setEmailFilter(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                disabled={loading || isSearching}
              />
              <TextField
                label={FormattedMessages.cell}
                value={numberFilter}
                onChange={(e) => setNumberFilter(e.target.value)}
                size="small"
                fullWidth
                variant="outlined"
                disabled={loading || isSearching}
              />
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              sx={{
                width: { xs: '100%', md: '75%' },
                marginX: 'auto',
              }}
            >
              <Button
                color="secondary"
                sx={{
                  textTransform: 'none',
                  textDecoration: 'underline',
                  fontWeight: 400,
                }}
                onClick={handleClearSearch}
                disabled={
                  loading ||
                  isSearching ||
                  (!nameFilter && !emailFilter && !numberFilter)
                }
              >
                {FormattedMessages.modalCancel}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleSearch}
                disabled={
                  loading ||
                  isSearching ||
                  (!nameFilter && !emailFilter && !numberFilter)
                }
              >
                {isSearching
                  ? FormattedMessages.loading
                  : FormattedMessages.searchButton}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Stack direction="row" justifyContent="flex-start" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/account/users/new')}
        >
          {FormattedMessages.addNewUser}
        </Button>
      </Stack>

      <TableContainer
        sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
      >
        <Table sx={{ borderRadius: 0 }}>
          <TableHead>
            <TableRow>
              <TableCell>{FormattedMessages.name}</TableCell>
              <TableCell>{FormattedMessages.email}</TableCell>
              <TableCell>{FormattedMessages.cell}</TableCell>
              <TableCell>{FormattedMessages.status}</TableCell>
              <TableCell>{FormattedMessages.type}</TableCell>
              <TableCell>{FormattedMessages.maxDailyMessage}</TableCell>
              <TableCell>{FormattedMessages.costCentersTitle}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderUsersTable()}</TableBody>
        </Table>
      </TableContainer>

      <Paginator
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        itemCount={totalCount}
        pageLength={pageLength}
        changeEvent={pageChange}
        editFunction={changePageLength}
        showTotal={FormattedMessages.usersFound}
        disabled={loading}
      />
    </Layout>
  );
};

export default WebSMSUsers;
